<template>
  <div id="watchlist-table">
    <v-card class="elevation-0 px-0 my-0 pb-2">
      <MarketsWarning :positions="watches" />

      <v-data-table
        id="watchlist-data-table"
        :headers="headers"
        :items="watches"
        class="elevation-0 left-sticky-table pt-3"
        mobile-breakpoint="0"
        :loading="loading"
        :no-data-text="
          type
            ? $t(`views.watchList.watchListTable.noWatches.${type}`)
            : $t(`views.watchList.watchListTable.noWatches.all`)
        "
        item-key="_id"
        :item-class="itemRowBackground"
        :page.sync="currentPage"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20, 30, 50, 100],
        }"
        :items-per-page="itemsPerPage"
        :dense="user.preferences.denseTables"
      >
        <template v-slot:item.ticker="{ item }">
          <TableTicker :position="item" notLinkToDetail @tickerClicked="editWatch(item)" />
        </template>
        <template v-slot:item.name="{ item }">
          <span v-if="item.name">{{ item.name }}</span>
          <span v-else></span>
        </template>
        <template v-slot:item.type="{ item }">
          <span>{{ $t(`common.positionTypes.${item.type}`) }}</span>
        </template>
        <template v-slot:item.country="{ item }">
          {{
            $te(`common.countries.${item.country}`)
              ? $t(`common.countries.${item.country}`) + ' (' + item.country + ')'
              : item.country
              ? item.country
              : '-'
          }}
        </template>
        <template v-slot:item.sector="{ item }">
          <span v-if="item.sector">{{ item.sector }}</span>
          <span v-else></span>
        </template>
        <template v-slot:item.manager="{ item }">
          <span v-if="item.manager">{{ item.manager }}</span>
          <span v-else></span>
        </template>
        <template v-slot:item.tags="{ item }">
          <TagGroup v-if="item.tags.length" :tags="item.tags" @tagClicked="tagClicked" />
          <span v-else></span>
        </template>

        <template v-slot:item.marketPrice="{ item }">
          <PositionMarketPrice type="watch" :position="item" />
        </template>

        <template v-slot:item.market.intraDay.changeP="{ item }">
          <TableNetPercent
            v-if="
              item.market &&
              item.market.intraDay &&
              (item.market.intraDay.changeP || item.market.intraDay.changeP === 0)
            "
            :value="item.market.intraDay.changeP"
            type="percent"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.market.hday.days30.change="{ item }">
          <TableNetPercent
            v-if="getHdayChange(item, 'days30')"
            :value="getHdayChange(item, 'days30')"
            type="percent"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.market.hday.ytd.change="{ item }">
          <TableNetPercent
            v-if="getHdayChange(item, 'ytd')"
            :value="getHdayChange(item, 'ytd')"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.market.hday.years1.change="{ item }">
          <TableNetPercent
            v-if="getHdayChange(item, 'years1')"
            :value="getHdayChange(item, 'years1')"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.market.hday.years3.change="{ item }">
          <TableNetPercent
            v-if="getHdayChange(item, 'years3')"
            :value="getHdayChange(item, 'years3')"
            type="percent"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.market.hday.years5.change="{ item }">
          <TableNetPercent
            v-if="getHdayChange(item, 'years5')"
            :value="getHdayChange(item, 'years5')"
            type="percent"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.targets.bidPrice.price="{ item }">
          <div>
            <currency
              v-if="item.targets.bidPrice.price"
              :value="item.targets.bidPrice.price"
              :currency="item.currency"
            />
            <span v-else></span>
          </div>
        </template>

        <template v-slot:item.bidPriceDistancePercent="{ item }">
          <TableNetPercent
            v-if="
              (item.bidPriceDistancePercent && item.bidPriceDistancePercent > -100) ||
              item.bidPriceDistancePercent === 0
            "
            :value="item.bidPriceDistancePercent"
            type="percent"
            :showArrow="false"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.targets.askPrice.price="{ item }">
          <div>
            <currency
              v-if="item.targets.askPrice.price"
              :value="item.targets.askPrice.price"
              :currency="item.currency"
            />
            <span v-else></span>
          </div>
        </template>

        <template v-slot:item.askPriceDistancePercent="{ item }">
          <TableNetPercent
            v-if="
              (item.askPriceDistancePercent && item.askPriceDistancePercent > -100) ||
              item.askPriceDistancePercent === 0
            "
            :value="item.askPriceDistancePercent"
            type="percent"
            :showArrow="false"
          />
          <span v-else></span>
        </template>

        <template v-slot:item.notes="{ item }">
          <span v-if="item.notes">
            {{ item.notes }}
          </span>
          <span v-else></span>
        </template>

        <template v-slot:item.isInPositions="{ item }">
          <template v-if="item.isInPositions">
            <v-icon small color="softGreen">
              mdi-briefcase-check-outline
            </v-icon>
          </template>
          <template v-else>
            <v-icon small color="softTextGrey">
              mdi-briefcase-off-outline
            </v-icon>
            <!-- <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  small
                  color="softTextGrey"
                  v-on="on"
                  @click="createPositionFromWatch(item)"
                >
                  mdi-briefcase-off-outline
                </v-icon>
              </template>
              <span>{{ $t(`views.watchList.watchListTable.createPositionFromWatch`) }}</span>
            </v-tooltip> -->
          </template>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" size="18" class="ml-2" v-on="on" @click="editWatch(item)">
                mdi-pencil-outline
              </v-icon>
            </template>
            <span>{{ $t('common.edit') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" size="18" class="ml-2" v-on="on" @click="deleteWatch(item)">
                mdi-delete-outline
              </v-icon>
            </template>
            <span>{{ $t('common.delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <HeadersModal
      v-if="isHeadersModalActive"
      :isModalActive.sync="isHeadersModalActive"
      :tableName="tableName"
      :defaultHeaders="defaultHeaders"
    />
  </div>
</template>

<script>
import { get } from 'lodash'
import { mapState } from 'vuex'
import { getWatches, deleteWatch } from '@/lib/api.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import Currency from '@/views/components/common/Currency'
import TableNetPercent from '@/views/components/common/TableNetPercent'
import TagGroup from '@/views/components/common/TagGroup'
import MarketsWarning from '@/views/components/common/positions/MarketsWarning'
import PositionMarketPrice from '@/views/components/common/positions/PositionMarketPrice'
import TableTicker from '@/views/components/common/TableTicker'
import gtag from '@/lib/gtag'

export default {
  components: {
    Currency,
    TableNetPercent,
    TagGroup,
    MarketsWarning,
    PositionMarketPrice,
    TableTicker,
  },
  mixins: [tableMixin],
  props: {
    type: {
      type: String,
      default: null,
    },
    filters: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    currentPage: null,
    loading: false,
    watches: [],
    tableName: 'watchList',
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    itemsPerPage() {
      return this.user.preferences.defaultItemsPerPage
    },
    defaultHeaders() {
      return [
        {
          text: this.$t(`views.watchList.watchListTable.ticker`),
          value: 'ticker',
          divider: true,
          customHideDisabled: true,
          customDragDisabled: true,
        },
        { text: this.$t('views.watchList.watchListTable.name'), value: 'name' },
        { text: this.$t('views.watchList.watchListTable.type'), value: 'type', customHidden: true },
        {
          text: this.$t('views.watchList.watchListTable.country'),
          value: 'country',
          customHidden: true,
        },
        {
          text: this.$t('views.watchList.watchListTable.sector'),
          value: 'sector',
          customHidden: true,
        },
        {
          text: this.$t('views.portfolio.positionsTable.manager'),
          value: 'manager',
          customHidden: true,
        },
        { text: this.$t('views.watchList.watchListTable.tags'), value: 'tags' },
        {
          text: `${this.$t('views.watchList.watchListTable.marketPrice')}`,
          value: 'marketPrice',
          align: 'right',
          sortable: false,
        },
        {
          text: `${this.$t('views.watchList.watchListTable.changeP')}`,
          value: 'market.intraDay.changeP',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.change.days30')}`,
          value: 'market.hday.days30.change',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.change.ytd')}`,
          value: 'market.hday.ytd.change',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.change.years1')}`,
          value: 'market.hday.years1.change',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.change.years3')}`,
          value: 'market.hday.years3.change',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.change.years5')}`,
          value: 'market.hday.years5.change',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.bidPrice')}`,
          value: 'targets.bidPrice.price',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.bidPriceDistancePercent')}`,
          value: 'bidPriceDistancePercent',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.askPrice')}`,
          value: 'targets.askPrice.price',
          align: 'right',
        },
        {
          text: `${this.$t('views.watchList.watchListTable.askPriceDistancePercent')}`,
          value: 'askPriceDistancePercent',
          align: 'right',
        },
        {
          text: this.$t('views.watchList.watchListTable.notes'),
          value: 'notes',
        },
        {
          text: this.$t('views.watchList.watchListTable.isInPositions'),
          value: 'isInPositions',
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'right',
          customHideDisabled: true,
          customSecondaryText: this.$t('views.watchList.watchListTable.editRemove'),
        },
      ]
    },
  },
  watch: {
    async type() {
      await this.initWatches()
    },
    filters: {
      async handler() {
        await this.initWatches()
      },
      deep: true,
    },
  },
  async created() {
    await this.initWatches()
  },
  methods: {
    tagClicked(tag) {
      if (tag) this.$emit('filterByTag', tag)
    },
    itemRowBackground(item) {},
    getHdayChange(item, type) {
      return get(item, `market.hday.${type}.change`, null)
    },
    async initWatches({ notChangePage } = {}) {
      this.loading = true
      try {
        this.watches = await this.loadWatches(this.filters)
        if (!notChangePage) this.currentPage = 1
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    async loadWatches(filters) {
      const watches = await getWatches({
        filter: {
          type: this.type ? this.type : undefined,
          ...filters,
        },
      })
      return watches.data
    },
    editWatch(watch) {
      this.$emit('editWatch', watch)
    },
    async deleteWatch(watch) {
      if (
        await this.$root.$confirm({
          title: this.$t('views.watchList.watchModal.confirm.title'),
          message: this.$t('views.watchList.watchModal.confirm.message'),
        })
      ) {
        this.loading = true
        try {
          await deleteWatch(watch)
          await this.$emit('loadAll')

          this.$root.$snackbar.success({ text: this.$t('common.deleteOk') })
          gtag.pageview({
            pagePath: `/watchlist/delete/success`,
            pageTitle: `watchlist|delete|success`,
            pageContentGroup: 'modal',
          })
        } catch (error) {
          this.$root.$snackbar.error({ text: this.$t('common.deleteError') })
          gtag.pageview({
            pagePath: `/watchlist/delete/error`,
            pageTitle: `watchlist|delete|error`,
            pageContentGroup: 'modal',
          })
        }
        this.loading = false
      }
    },
    // async createPositionFromWatch(watch) {
    //   try {
    //     const positionAttributes = [
    //       'type',
    //       'ticker',
    //       'country',
    //       'exchange',
    //       'currency',
    //       'name',
    //       'sector',
    //       'tags',
    //     ]
    //     const position = pick(watch, positionAttributes)
    //     await createPosition(position)
    //     this.initWatches({ notChangePage: true })
    //     this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
    //   } catch (error) {
    //     this.error = get(error, 'data.name', null)
    //     if (!this.error) {
    //       this.$root.$snackbar.error({ text: this.$t('common.saveError') })
    //     } else {
    //       this.$root.$snackbar.error({ text: this.$t(`apiErrors.${this.error}`) })
    //     }
    //   }
    // },
  },
}
</script>

<style lang="scss">
#watchlist-table {
  #watchlist-data-table {
    .items-flex {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      &.ticker-column {
        justify-content: flex-start;
        align-items: flex-start;
      }
    }
  }
}
</style>
